import * as React from "react"
import { useToast } from "@/hooks/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"
import { useAnnounce } from "@/components/ui/sr-announcer"

export function Toaster() {
  const { toasts } = useToast()
  const { announce } = useAnnounce()
  const announcedToastsRef = React.useRef<Set<string>>(new Set())

  // Announce new toasts to screen readers
  React.useEffect(() => {
    toasts.forEach((toast: any) => {
      // Only announce each toast once
      if (!announcedToastsRef.current.has(toast.id)) {
        announcedToastsRef.current.add(toast.id)
        
        // Build announcement message from title and description
        const parts: string[] = []
        if (toast.title) {
          parts.push(typeof toast.title === 'string' ? toast.title : '')
        }
        if (toast.description) {
          parts.push(typeof toast.description === 'string' ? toast.description : '')
        }
        
        const message = parts.filter(Boolean).join('. ')
        if (message) {
          // Use assertive for destructive/error toasts, polite for others
          const isError = toast.variant === 'destructive'
          announce(message, isError)
        }
      }
    })
    
    // Clean up announced toasts that are no longer in the list
    const currentIds = new Set(toasts.map((t: any) => t.id))
    announcedToastsRef.current.forEach(id => {
      if (!currentIds.has(id)) {
        announcedToastsRef.current.delete(id)
      }
    })
  }, [toasts, announce])

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, ...props }: any) {
        return (
          <Toast key={id} {...props}>
            <div className="grid gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
